import React, { useContext, useState } from 'react';
import { Card, CardContent, Fade } from '@mui/material';
import { selectInventory } from '@/redux/selectors/orderPlacementSelectors';
import InventoryPreviewModal from './InventoryPreviewModal';
import InventoryFile from '@/components/_common/InventoryFile/InventoryFile';
import FileTypeDisplay from '../../_common/FileTypeDisplay';
import NoInventory from './NoInventory';
import NoInventoryV2 from '@/components/TitleDetail/v2/TitleDetailInventoryV2/NoInventoryV2';
import { selectFileTypeGroupings, selectFileTypeGroupingsFromApi } from '@/redux/selectors/constantsSelectors';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import { ConfigContext } from '@/contextAPI/configContext';

type InventoryListProps = {
  disableDownloads?: boolean;
  filterType?: string | null;
  disableSelected?: boolean;
  canDelete?: boolean;
  previewOnly?: boolean;
  onClickFile?: (file: InventoryFileEntity) => void;
};

const INVENTORY_TAGS_FILE_TYPE_MAP: Record<string, string[]> = {
  audio: ['discrete audio']
};

const InventoryList = ({ disableDownloads = false, filterType = null, disableSelected = false, canDelete, previewOnly, onClickFile }: InventoryListProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const inventory = useAppSelector(selectInventory) as InventoryFileEntity[];
  const fileTypeGroupingsFromCommon = useAppSelector(selectFileTypeGroupings);
  const fileTypeGroupingsFromApi = useAppSelector(selectFileTypeGroupingsFromApi);
  const fileTypeGroupings = fileTypeGroupingsFromApi ?? fileTypeGroupingsFromCommon;
  const allFileTypes = [...fileTypeGroupings.video, ...fileTypeGroupings.audio, ...fileTypeGroupings.subtitle, ...fileTypeGroupings.image];
  const [fadeTime, setFadeTime] = useState(300);

  const cancelFade = () => {
    if (fadeTime > 0) setFadeTime(0);
  };

  const FileTypeContainer = React.memo(({ title, files = [] }: { title: string; files: InventoryFileEntity[] }) => {
    if (!files.length && filterType && filterType !== 'all') {
      return isTitleV2 ? <NoInventoryV2 type={title} /> : <NoInventory type={title} />;
    }
    if (!files.length) return null;
    return (
      <Card sx={{ mx: 0, my: 2 }} variant="outlined" elevation={0}>
        <CardContent>
          <FileTypeDisplay chip={false} type={title} sx={{ mb: 1.5 }} />
          <div>
            {files.map((file, i) => (
              <Fade in key={`${file.id}_${file.version_external_id}`} timeout={(i + 1) * fadeTime}>
                <div>
                  <InventoryFile
                    file={file}
                    onClick={onClickFile ? () => onClickFile(file) : undefined}
                    disableDownloads={disableDownloads}
                    disableSelected={disableSelected}
                    afterFileSelection={cancelFade}
                    canDelete={canDelete}
                    previewOnly={previewOnly}
                  />
                </div>
              </Fade>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  });

  const filterInventory = (type: string, file: InventoryFileEntity) => {
    // type: keyof FILE_TYPE_GROUPING
    if (file.file_category) return file.file_category === type;
    if (type === 'other') return !allFileTypes.includes(file.file_type);

    if (file.inventory_tags?.length) {
      const fileInventoryTagValues = new Set(file.inventory_tags.map((tag) => tag?.value?.toLowerCase()).filter(Boolean));
      const inventoryTagMatches = Object.entries(INVENTORY_TAGS_FILE_TYPE_MAP).filter(([, tagValues]) =>
        tagValues.some((tagValue) => fileInventoryTagValues.has(tagValue))
      );

      // if inventory tag value of the file matches one of the mapping entries, verify if type in the mapping matches the requested type
      if (inventoryTagMatches.length) {
        return inventoryTagMatches.some(([tagType]) => tagType === type);
      }
    }

    return fileTypeGroupings[type as keyof typeof fileTypeGroupings].includes(file.file_type);
  };

  if (!inventory.length) return null;

  return (
    <>
      {!filterType || filterType === 'all' ? (
        <>
          <FileTypeContainer title="Video" files={inventory.filter((file) => filterInventory('video', file))} />
          <FileTypeContainer title="Audio" files={inventory.filter((file) => filterInventory('audio', file))} />
          <FileTypeContainer title="Subtitle" files={inventory.filter((file) => filterInventory('subtitle', file))} />
          <FileTypeContainer title="Image" files={inventory.filter((file) => filterInventory('image', file))} />
          <FileTypeContainer title="Other" files={inventory.filter((file) => filterInventory('other', file))} />
        </>
      ) : (
        <FileTypeContainer title={filterType} files={inventory.filter((file) => filterInventory(filterType, file))} />
      )}

      <InventoryPreviewModal />
    </>
  );
};

export default React.memo(InventoryList);